// React
import { useCallback, useEffect, useState, MutableRefObject } from "react";

// MUI
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridGroupingColDefOverride, GridValidRowModel } from "@mui/x-data-grid-premium";
import { Fade, SxProps } from "@mui/material";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";

// Helpers
import { tableKeys } from "@/Constants/tableKeys";
import { generateSx, getRowClassName } from "@Layouts/components/Tables/DataSmartTable/helpers";

// Types
import { ICustomerRegistrationActivity } from "@/DataPages/components/Inventory/types/dealerTrades";
import {
	useConsumerRegistrationStatusColumns,
	groupingColDef,
} from "@DataPages/components/Inventory/hooks/useConsumerRegistrationStatusColumns";

type ConsumerRegistrationStatusTableProps = {
	apiRef: MutableRefObject<GridApiPremium>;
	hierarchy?: string;
	isLoading: boolean;
	hasNextPage: boolean;
	rows: ICustomerRegistrationActivity[];
	tableTitle: string;
};
const TREE_DATA_GROUPING_FIELD = "__tree_data_group__";

export default function ConsumerRegistrationStatusTable(props: Readonly<ConsumerRegistrationStatusTableProps>) {
	const { rows, apiRef, tableTitle, hasNextPage, isLoading } = props;

	const columns = useConsumerRegistrationStatusColumns(apiRef);

	const [sx, setSx] = useState<SxProps>({});

	const groupingColDefinition: GridGroupingColDefOverride = groupingColDef;

	const getTreeDataPath = useCallback((row: GridValidRowModel) => row.hierarchy as string[], []);

	useEffect(() => {
		if (!rows.length) {
			setSx({ overflow: "hidden" });
			return;
		}

		setSx(generateSx(apiRef, { yGap: "0px" }));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [apiRef, rows]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.CONSUMER_REGISTRATION,
			}}
			apiRef={apiRef}
			treeData
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDefinition}
			rows={rows ?? []}
			headerHeight={40}
			rowHeight={25}
			maxRows={21}
			columns={columns}
			disableRowSelectionOnClick
			disableColumnResize
			disableColumnMenu
			hideFooter
			enableCustomScrollbars
			columnVisibilityModel={{ _id: false }}
			pinnedColumns={{
				left: [
					TREE_DATA_GROUPING_FIELD,
					"saleDate",
					"saleType",
					"dealType",
					"dealStatus",
					"dealNumber",
					"stockNumber",
					"salesEmployeeName",
					"dealManagerName",
					"financeManagerName",
					"vehicleVin",
					"units",
					"__empty",
				],
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						hide: hasNextPage || isLoading,
						title: tableTitle,
						titleClasses: "page-title",
						className: "!pt-[14px] !pb-[23px]",
					},
					customComponent: (
						<Fade in={hasNextPage || isLoading} timeout={400}>
							<div>
								<LoadingSpinner label="Consumer registration status data is still loading..." className="items-center" />
							</div>
						</Fade>
					),
					filter: {
						disabled: false,
					},
					search: {
						data: rows || [],
						hide: false,
						disabled: false,
					},
				},
				noRowsOverlay: {
					title: "No consumer registration status data",
					className: "!pb-[5px]",
				},
			}}
			sx={{
				...sx,
				"& .MuiDataGrid-pinnedColumnHeader--left .MuiDataGrid-pinnedColumnHeader--right": {
					border: "none",
				},
				"& .MuiDataGrid-columnHeader": {
					border: "none !important",
				},
				"& .MuiDataGrid-filler--pinnedLeft": {
					display: "none !important",
				},
			}}
			getRowClassName={getRowClassName}
			customProps={{
				tableContainerClassName: "[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
				tableClassName: {
					"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell:not(.empty-row)]:shadow-table-hov": true,
					"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row))_.MuiDataGrid-cell:not(.empty-row)]:shadow-table-line": true,
				},
				expandableRows: false,
				hide: false,
			}}
		/>
	);
}
